import { memo, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Repeat2, Reply, CornerDownRight, Rss, Pin } from 'lucide-react'
import { nip19 } from 'nostr-tools'
import { PostActionsMenu } from '@/components/PostActionsMenu'
import { FollowConfirmModal } from '@/components/FollowConfirmModal'
import { Avatar } from '@/components/Avatar'
import { PostAuthorRow } from '@/components/PostAuthorRow'
import { PostEngagementBar } from '@/components/PostEngagementBar'
import { ParentPostPreview } from '@/components/ParentPostPreview'
import { QuotedPostEmbed } from '@/components/QuotedPostEmbed'
import { FeedEmbedCard } from '@/components/FeedEmbedCard'
import { RichText } from '@/components/RichText'
import { PollCard } from '@/components/PollCard'
import { ContentWarning } from '@/components/ContentWarning'
import { PostEmbedDisplay } from '@/components/PostEmbedDisplay'
import { HashtagAttributionBanner } from './HashtagAttributionBanner.component'
import { useAuth } from '@/hooks/useAuth'
import { useFeedTuning } from '@/hooks/useFeedTuning'
import { useCrossProtocol } from '@/contexts/CrossProtocolContext/CrossProtocolContext'
import { useFollowUser } from '@/hooks/useFollowUser'
import {
    FullAPContentExpander,
    isTruncatedBridgedPost,
    getBridgedPostDisplayText,
    hasQuoteCardReference,
} from '@/components/FullAPContentExpander'
import { isSelfReferencingBridgedCard } from '@/lib/bridgedContent'
import { useToast } from '@/hooks/useToast'
import { usePlatform } from '@/hooks/usePlatform'
import type { Platform } from '@/contexts/ProtocolContext/ProtocolContext'
import { getPostBridgeInfo } from '@/lib/bridge'
import { buildShareUrl } from '@/lib/shareUtils'
import type { PostCardProps } from './PostCard.types'
import { strings } from '@/strings'

export const PostCard = memo(function PostCard({
    post,
    isOwnPost,
    threadPosition,
    hideActions,
    hideAuthor,
    onLike,
    onRepost,
    onQuote,
    onDelete,
    onReply,
    onBookmark,
    onTogglePin,
    onAuthorClick,
    onPostClick,
}: PostCardProps) {
    const navigate = useNavigate()

    const {
        isAuthenticated,
        session,
        mastodonSession,
        pixelfedSession,
        nostrSession,
        currentMastodonHandle,
        currentPixelfedHandle,
    } = useAuth()
    const [showFollowConfirm, setShowFollowConfirm] = useState(false)
    const { showToast } = useToast()
    const { connectedAccounts, selectedProtocol } = usePlatform()
    const { showAllAccounts } = useCrossProtocol()
    const bridgeInfo = getPostBridgeInfo(post.author, post.bridgyOriginalUrl)
    const feedTuning = useFeedTuning(post)

    // Whether the current user is the one who reposted/boosted this post, so we
    // can attribute it to "You" rather than the user's own name/handle.
    const isCurrentUser = (author?: { did?: string; handle?: string }): boolean => {
        if (!author) return false
        if (author.did && session?.did && author.did === session.did) return true
        const handle = author.handle?.toLowerCase()
        if (!handle) return false
        return handle === currentMastodonHandle || handle === currentPixelfedHandle
    }
    const isOwnRepost = isCurrentUser(post.repostedBy)
    const isOwnBoost = isCurrentUser(post.boostedBy)

    const postPlatform: Platform =
        post.protocol === 'nostr'
            ? 'nostr'
            : post.isActivityPub
              ? (post.protocol ?? 'mastodon')
              : 'bluesky'
    const postIdentifier =
        isOwnPost || hideAuthor || !!bridgeInfo
            ? undefined
            : post.isActivityPub
              ? post.author?.handle
              : post.author?.did
    const {
        // isFollowing: isFollowingAuthor,
        // isLoading: isFollowLoading,
        handleToggleFollow,
    } = useFollowUser(
        postPlatform,
        selectedProtocol === postPlatform ||
            ((selectedProtocol === 'mastodon' || selectedProtocol === 'pixelfed') &&
                (postPlatform === 'mastodon' || postPlatform === 'pixelfed'))
            ? postIdentifier
            : undefined,
        {
            initialFollowingUri: post.author?.followingUri,
        }
    )
    // Temporarily disabled while we work through issues with the inline
    // follow affordance; uncomment the block below (and the destructures
    // above) to re-enable.
    const showFollowButton = false
    // const showFollowButton =
    //     isAuthenticated &&
    //     !isOwnPost &&
    //     !hideAuthor &&
    //     !bridgeInfo &&
    //     !isFollowingAuthor &&
    //     !isFollowLoading

    const shareUrl = buildShareUrl(post)
    const handleShare = shareUrl
        ? async () => {
              try {
                  if (navigator.share) {
                      await navigator.share({ url: shareUrl })
                  } else {
                      await navigator.clipboard.writeText(shareUrl)
                      showToast(strings.posts.linkCopied, 'success')
                  }
              } catch (err) {
                  if (err instanceof Error && err.name === 'AbortError') return
                  showToast(strings.posts.couldNotCopyLink, 'error')
              }
          }
        : undefined

    const nostrNevent = useMemo(() => {
        if (post.protocol !== 'nostr' || !post.uri) return null
        const id = post.uri.startsWith('nostr:') ? post.uri.slice(6) : post.uri
        try {
            return nip19.neventEncode({ id })
        } catch {
            return null
        }
    }, [post.protocol, post.uri])

    // In cross-protocol mode, allow engagement if we have a session for this post's protocol
    const hasSessionForPostProtocol =
        postPlatform === 'bluesky'
            ? !!session
            : postPlatform === 'mastodon'
              ? !!mastodonSession
              : postPlatform === 'pixelfed'
                ? !!pixelfedSession
                : postPlatform === 'nostr'
                  ? !!nostrSession
                  : false

    // Engagement requires the active account's protocol to match the post's protocol.
    // ActivityPub (mastodon, pixelfed) ↔ ActivityPub: OK. AT Protocol (bluesky) ↔ AT Protocol: OK.
    // Mismatches (bluesky account + mastodon post, etc.) must be disabled — the adapter
    // has no session for the other protocol and the action would silently fail.
    const activeIsActivityPub = selectedProtocol === 'mastodon' || selectedProtocol === 'pixelfed'
    const isNostrPost = post.protocol === 'nostr'
    const isNostrActive = selectedProtocol === 'nostr'
    // Bridged AT Protocol posts (Bluesky user bridged to ActivityPub): allow engagement
    // from Mastodon/Pixelfed accounts via Bridgy Fed cross-protocol bridging.
    const isBridgedToAP = bridgeInfo?.isBridged && !bridgeInfo.isFromAP
    const protocolMatches =
        (isNostrPost && isNostrActive) ||
        (!isNostrPost && !isNostrActive && post.isActivityPub === activeIsActivityPub) ||
        (activeIsActivityPub && !!isBridgedToAP)
    const effectiveProtocolMatches =
        (showAllAccounts && hasSessionForPostProtocol) || protocolMatches
    const canEngage =
        effectiveProtocolMatches &&
        (isNostrPost ||
            (!post.isActivityPub && !!post.cid) ||
            (post.isActivityPub && (!!post.mastodonStatusId || !!post.uri)) ||
            (!!isBridgedToAP && (!!post.cid || !!post.uri)))
    const canQuote = canEngage && connectedAccounts.some(a => a.platform === postPlatform)
    const canDelete = canEngage
    // Offer Pin/Unpin only where the pin state is actually known. `isPinned` is
    // undefined on every surface that did not ask the server for pins, which keeps
    // the app from offering a blind re-pin (a 422 on Mastodon).
    const pinState: 'pinned' | 'unpinned' | undefined =
        isOwnPost &&
        !!onTogglePin &&
        canEngage &&
        !post.isRepost &&
        !post.boostedBy &&
        post.isPinned !== undefined
            ? post.isPinned
                ? 'pinned'
                : 'unpinned'
            : undefined
    // AT Protocol posts need rkey, AP posts need uri
    const canClick = !!onPostClick && (post.isActivityPub ? !!post.uri : !!post.rkey)
    const isLiked = !!post.likeUri
    const isReposted = !!post.repostUri
    const isBookmarked = !!post.bookmarkUri
    const hasVisibleParent =
        !!post.replyParentUri &&
        !post.replyParentNotFound &&
        !threadPosition?.parentIsAbove &&
        !hideAuthor

    const isApContext = selectedProtocol === 'mastodon' || selectedProtocol === 'pixelfed'

    const handleAuthorClick = () => {
        if (isApContext) {
            // AP context: stay on AP side
            const apIdentifier = bridgeInfo?.apHandle ?? post.author?.handle
            if (apIdentifier) void navigate(`/profile/${apIdentifier}`)
        } else if (bridgeInfo) {
            // AT context: navigate to the AT side of the bridged author
            const atIdentifier = bridgeInfo.isFromAP
                ? (post.author?.handle ?? post.author?.did)
                : bridgeInfo.bridgedHandle
            if (atIdentifier) void navigate(`/profile/${atIdentifier}`)
        } else if (onAuthorClick && post.author) {
            onAuthorClick(post.author.handle)
        }
    }

    const handlePostClick = (e: React.MouseEvent) => {
        if (!canClick) return
        // Don't navigate when clicking interactive elements (buttons, links, etc.)
        const target = e.target as HTMLElement
        if (target.closest('button, a, [role="button"], video')) return
        onPostClick?.(post)
    }

    return (
        <div
            className={`${hideAuthor ? 'pb-3' : 'py-3'} relative ${canClick ? 'cursor-pointer' : ''} ${canClick && !hasVisibleParent ? 'hover:bg-surface-light/50 dark:hover:bg-surface-dark/50' : ''}`}
            onClick={handlePostClick}
        >
            {/* Custom feed attribution */}
            {post.fromFeedName && (
                <div className="pb-1 flex items-center gap-1.5 text-text-secondary-light dark:text-text-secondary-dark text-xs pl-[42px]">
                    <Rss className="w-3.5 h-3.5 flex-shrink-0" />
                    {post.fromFeedUri ? (
                        <span>
                            {'From '}
                            <button
                                onClick={e => {
                                    e.stopPropagation()
                                    if (post.fromFeedUri!.includes('app.bsky.graph.list')) {
                                        void navigate(
                                            `/list/${encodeURIComponent(post.fromFeedUri!)}`
                                        )
                                    } else {
                                        void navigate(
                                            `/feed/${encodeURIComponent(post.fromFeedUri!)}`
                                        )
                                    }
                                }}
                                className="hover:underline"
                            >
                                {post.fromFeedName}
                            </button>
                        </span>
                    ) : (
                        <span>From {post.fromFeedName}</span>
                    )}
                </div>
            )}

            {/* Followed-hashtag attribution */}
            {post.viaFollowedHashtags?.length ? (
                <HashtagAttributionBanner tags={post.viaFollowedHashtags} />
            ) : null}

            {/* Pinned banner — reads pinnedEntry (a fact about the row), never isPinned */}
            {post.pinnedEntry && (
                <div className="pb-1 flex items-center gap-1.5 text-text-secondary-light dark:text-text-secondary-dark text-xs pl-[42px]">
                    <Pin className="w-3.5 h-3.5 flex-shrink-0" />
                    {strings.posts.pinned}
                </div>
            )}

            {/* Repost banner */}
            {post.isRepost && post.repostedBy && (
                <div className="pb-1 flex items-center gap-1.5 text-text-secondary-light dark:text-text-secondary-dark text-xs pl-[42px]">
                    <Repeat2 className="w-3.5 h-3.5 flex-shrink-0" />
                    <button
                        onClick={e => {
                            e.stopPropagation()
                            if (onAuthorClick && post.repostedBy) {
                                onAuthorClick(post.repostedBy.handle)
                            }
                        }}
                        className="hover:underline"
                    >
                        {isOwnRepost ? (
                            strings.posts.youReposted
                        ) : (
                            <>
                                {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty displayName should fall back to handle, not render blank */}
                                {post.repostedBy.displayName ||
                                    `@${post.repostedBy.displayHandle ?? post.repostedBy.handle}`}{' '}
                                {strings.posts.reposted}
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* Boost indicator (ActivityPub) */}
            {post.boostedBy && (
                <div className="pb-1 flex items-center gap-1.5 text-text-secondary-light dark:text-text-secondary-dark text-xs pl-[42px]">
                    <Repeat2 className="w-3.5 h-3.5 flex-shrink-0" />
                    <button
                        onClick={e => {
                            e.stopPropagation()
                            if (onAuthorClick && post.boostedBy) {
                                onAuthorClick(post.boostedBy.handle)
                            }
                        }}
                        className="hover:underline"
                        aria-label={
                            isOwnBoost
                                ? strings.posts.youBoosted
                                : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty displayName should fall back to handle, not render blank
                                  `Boosted by ${post.boostedBy.displayName || (post.boostedBy.displayHandle ?? post.boostedBy.handle)}`
                        }
                    >
                        {isOwnBoost ? (
                            strings.posts.youBoosted
                        ) : (
                            <>
                                {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty displayName should fall back to handle, not render blank */}
                                {post.boostedBy.displayName ||
                                    `@${post.boostedBy.displayHandle ?? post.boostedBy.handle}`}{' '}
                                {strings.posts.boosted}
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* Parent post preview for replies (suppressed when parent is above in thread group or when hideAuthor is set) */}
            {post.replyParentUri &&
                !post.replyParentNotFound &&
                !threadPosition?.parentIsAbove &&
                !hideAuthor && (
                    <ParentPostPreview
                        parentUri={post.replyParentUri}
                        relayHint={post.replyParentRelayHint}
                        post={post.parentPost}
                        onAuthorClick={onAuthorClick}
                        onPostClick={onPostClick}
                        onLike={onLike}
                        onRepost={onRepost}
                        onQuote={canQuote ? onQuote : undefined}
                        onBookmark={onBookmark}
                        onDelete={onDelete}
                    />
                )}

            {/* Deleted/blocked parent indicator */}
            {post.replyParentNotFound && !hideAuthor && (
                <div className="pb-1 flex items-center gap-1.5 text-text-secondary-light dark:text-text-secondary-dark text-xs pl-[42px]">
                    <CornerDownRight className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Replied to a post</span>
                </div>
            )}

            {/* Thread gap indicator - shown when posts were skipped in the group */}
            {threadPosition?.hasGapAbove && threadPosition.threadRootUri?.startsWith('at://') && (
                <button
                    onClick={e => {
                        e.stopPropagation()
                        // AT Protocol URI: at://did/collection/rkey
                        const parts = threadPosition.threadRootUri.split('/')
                        const rkey = parts[parts.length - 1]
                        const did = parts[2]
                        if (rkey && did) {
                            void navigate(`/profile/${did}/post/${rkey}`)
                        }
                    }}
                    className="flex items-center gap-3 px-4 py-1 text-xs text-primary hover:underline"
                >
                    <span className="flex-shrink-0 w-8 flex justify-center">
                        <span className="inline-block w-0.5 h-3 border-l-2 border-dashed border-border-light dark:border-border-dark" />
                    </span>
                    <span>View full thread</span>
                </button>
            )}

            <div
                className={`${hideAuthor ? '' : 'px-4 '}flex ${hideAuthor ? '' : 'gap-3 '}items-start ${canClick && hasVisibleParent ? 'hover:bg-surface-light/50 dark:hover:bg-surface-dark/50' : ''}`}
            >
                {/* Avatar with thread line connector */}
                {!hideAuthor && (
                    <div className="flex-shrink-0 flex flex-col items-center">
                        {/* Thread line UP: connects to previous post in group */}
                        {threadPosition?.isGrouped
                            ? (threadPosition.position === 'middle' ||
                                  threadPosition.position === 'last') && (
                                  <div className="w-0.5 h-2 bg-border-light dark:bg-border-dark -mt-2" />
                              )
                            : /* Original connector for non-grouped posts with visible parent */
                              hasVisibleParent && (
                                  <div className="w-0.5 h-2 bg-border-light dark:bg-border-dark -mt-2" />
                              )}
                        <div className="relative">
                            <button
                                onClick={e => {
                                    e.stopPropagation()
                                    handleAuthorClick()
                                }}
                                className="focus:outline-none"
                                disabled={!bridgeInfo && !onAuthorClick}
                            >
                                <Avatar
                                    src={post.author?.avatarUrl}
                                    alt={post.author?.handle ?? 'user'}
                                    size="sm"
                                />
                            </button>
                            {showFollowButton && (
                                <button
                                    type="button"
                                    onClick={e => {
                                        e.stopPropagation()
                                        setShowFollowConfirm(true)
                                    }}
                                    className="absolute bottom-0 right-0 translate-x-1/4 w-4 h-4 bg-black dark:bg-white rounded-full flex items-center justify-center text-white dark:text-black text-[10px] font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-white hover:scale-110 transition-transform z-10 after:content-[''] after:absolute after:-inset-2"
                                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty displayName/handle should fall through, not render blank
                                    aria-label={`Follow ${post.author?.displayName || post.author?.handle || 'user'}`}
                                >
                                    +
                                </button>
                            )}
                        </div>
                        {/* Thread line DOWN: connects to next post in group */}
                        {threadPosition?.isGrouped &&
                            (threadPosition.position === 'first' ||
                                threadPosition.position === 'middle') && (
                                <div className="w-0.5 flex-1 bg-border-light dark:bg-border-dark mt-1" />
                            )}
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                    {/* Author row */}
                    {!hideAuthor && (
                        <div className="flex items-center justify-between gap-2">
                            <PostAuthorRow
                                author={post.author}
                                createdAt={post.createdAt}
                                protocol={post.protocol}
                                isActivityPub={post.isActivityPub}
                                bridgeInfo={bridgeInfo}
                                onAuthorClick={handleAuthorClick}
                                onNativeHandleClick={() =>
                                    navigate(`/profile/${post.author?.handle}`)
                                }
                                onBridgedHandleClick={handleAuthorClick}
                                authorClickDisabled={!bridgeInfo && !onAuthorClick}
                            />

                            <div className="flex items-center">
                                {/* More menu (for own posts and admins) */}
                                {isAuthenticated &&
                                    ((isOwnPost && canDelete) ||
                                        !!pinState ||
                                        post.protocol !== 'nostr' ||
                                        (post.protocol === 'nostr' && !!nostrNevent)) && (
                                        <PostActionsMenu
                                            isOwnPost={isOwnPost}
                                            canDelete={canDelete}
                                            onDelete={() => onDelete?.(post)}
                                            pinState={pinState}
                                            onTogglePin={
                                                pinState ? () => onTogglePin?.(post) : undefined
                                            }
                                            text={post.text}
                                            showCopyUri={post.protocol !== 'nostr' && !!post.author}
                                            uri={post.uri}
                                            showAtExplore={
                                                post.protocol !== 'nostr' && !!post.author
                                            }
                                            isActivityPub={post.isActivityPub}
                                            authorHandle={post.author?.handle}
                                            rkey={post.rkey}
                                            nostrNevent={
                                                post.protocol === 'nostr' ? nostrNevent : null
                                            }
                                            feedTuning={feedTuning}
                                        />
                                    )}
                            </div>
                        </div>
                    )}

                    {/* Reply indicator (only when no parent preview) */}
                    {post.isReply && !post.replyParentUri && (
                        <div className="flex items-center gap-1 text-text-secondary-light dark:text-text-secondary-dark text-xs mt-0.5">
                            <Reply className="w-3 h-3" />
                            <span>Reply</span>
                        </div>
                    )}

                    {/* Post content — wrapped by content warning when present */}
                    {post.contentWarning?.scope === 'content' ? (
                        <ContentWarning text={post.contentWarning.text}>
                            {post.text && (
                                <div className="mt-1">
                                    <RichText
                                        text={post.text}
                                        facets={post.facets}
                                        className="text-sm text-text-primary-light dark:text-text-primary-dark"
                                    />
                                </div>
                            )}
                            {post.embed && (
                                <div className="mt-2" onClick={e => e.stopPropagation()}>
                                    <PostEmbedDisplay
                                        embed={post.embed}
                                        contentWarning={post.contentWarning}
                                        isTruncated={isTruncatedBridgedPost(post.text)}
                                        excludeFediverse={isSelfReferencingBridgedCard(post)}
                                    />
                                </div>
                            )}
                            {post.quotedPost && (
                                <div className="mt-2">
                                    <QuotedPostEmbed
                                        post={post.quotedPost}
                                        onAuthorClick={onAuthorClick}
                                        onPostClick={onPostClick}
                                    />
                                </div>
                            )}
                            {post.feedEmbed && (
                                <div className="mt-2">
                                    <FeedEmbedCard
                                        feed={post.feedEmbed}
                                        onClick={uri =>
                                            navigate(`/feed/${encodeURIComponent(uri)}`)
                                        }
                                    />
                                </div>
                            )}
                        </ContentWarning>
                    ) : (
                        <>
                            {/* Post text — FullAPContentExpander handles truncated bridged posts */}
                            {post.text && (
                                <div className="mt-1">
                                    <FullAPContentExpander
                                        bridgyOriginalUrl={post.bridgyOriginalUrl}
                                        text={post.text}
                                        hasQuoteReference={hasQuoteCardReference(post)}
                                    >
                                        <RichText
                                            text={getBridgedPostDisplayText(post)}
                                            facets={post.facets}
                                            className="text-sm text-text-primary-light dark:text-text-primary-dark"
                                        />
                                    </FullAPContentExpander>
                                </div>
                            )}

                            {/* Embed — hide "Original post on..." embed on truncated bridged posts */}
                            {post.embed && (
                                <div className="mt-2" onClick={e => e.stopPropagation()}>
                                    <PostEmbedDisplay
                                        embed={post.embed}
                                        contentWarning={post.contentWarning}
                                        isTruncated={isTruncatedBridgedPost(post.text)}
                                        excludeFediverse={isSelfReferencingBridgedCard(post)}
                                    />
                                </div>
                            )}

                            {/* Quoted post */}
                            {post.quotedPost && (
                                <div className="mt-2">
                                    <QuotedPostEmbed
                                        post={post.quotedPost}
                                        onAuthorClick={onAuthorClick}
                                        onPostClick={onPostClick}
                                    />
                                </div>
                            )}

                            {/* Feed embed */}
                            {post.feedEmbed && (
                                <div className="mt-2">
                                    <FeedEmbedCard
                                        feed={post.feedEmbed}
                                        onClick={uri =>
                                            navigate(`/feed/${encodeURIComponent(uri)}`)
                                        }
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {/* Poll — rendered once outside content warning blocks */}
                    {post.poll && <PollCard post={post} poll={post.poll} />}

                    {/* Action bar */}
                    {!hideActions && isAuthenticated && (
                        <div className="mt-2">
                            <PostEngagementBar
                                layout="split"
                                size="sm"
                                disabled={!canEngage}
                                isActivityPub={post.isActivityPub}
                                replyCount={post.metrics.replyCount}
                                onReply={onReply ? () => onReply(post) : undefined}
                                repostCount={post.metrics.repostCount}
                                isReposted={isReposted}
                                onRepost={onRepost ? () => onRepost(post) : undefined}
                                canQuote={canQuote}
                                onQuote={canQuote && onQuote ? () => onQuote(post) : undefined}
                                likeCount={post.metrics.likeCount}
                                isLiked={isLiked}
                                onLike={onLike ? () => onLike(post) : undefined}
                                onShare={handleShare}
                                isBookmarked={isBookmarked}
                                onBookmark={onBookmark ? () => onBookmark(post) : undefined}
                            />
                        </div>
                    )}
                </div>
            </div>

            {post.author?.handle && (
                <FollowConfirmModal
                    isOpen={showFollowConfirm}
                    onClose={() => setShowFollowConfirm(false)}
                    onConfirm={() => {
                        handleToggleFollow({
                            onSuccess: () =>
                                showToast(`Followed @${post.author!.handle}`, 'success'),
                            onError: () => showToast(strings.posts.couldNotFollow, 'error'),
                        })
                    }}
                    handle={post.author.handle}
                    displayName={post.author.displayName}
                />
            )}
        </div>
    )
})
